import { useCallback, useEffect, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  Alert02Icon,
  BookOpen01Icon,
  Delete02Icon,
  Edit02Icon,
  Loading02Icon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import { api, apiError } from '@/lib/api';
import { fmtDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const EMPTY_FORM = { title: '', category: 'General', content: '', active: true };

// AiContextManager renders the "Contexto IA" tab: the institutional knowledge base
// (a lightweight RAG) that feeds the AI-assisted demand→initiative conversion.
// Admin-only. Editing a document here changes how every future conversion is drafted.
export default function AiContextManager() {
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null); // doc being edited, or 'new'
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/ai-context');
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(apiError(e));
      setDocs([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = docs?.filter((d) => d.active).length ?? 0;

  return (
    <section aria-labelledby="ai-context-heading">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-2xl">
          <h2 id="ai-context-heading" className="font-display text-2xl font-bold text-foreground">
            Contexto IA (RAG institucional)
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Documentos de contexto sobre la adopción de IA en UC CHRISTUS. VIA los lee cada vez que un requerimiento de
            la bandeja de demanda se convierte en iniciativa, para inferir mejor el modelo, los datos, el riesgo y el
            valor.
          </p>
        </div>
        <Button
          className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => setEditing('new')}
          aria-label="Agregar documento de contexto">
          <HugeiconsIcon icon={Add01Icon} size={16} strokeWidth={1.5} className="mr-1.5" aria-hidden="true" />
          Nuevo documento
        </Button>
      </div>

      {/* Impact warning */}
      <div className="mt-5 flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/5 p-4" role="note">
        <HugeiconsIcon
          icon={Alert02Icon}
          size={18}
          strokeWidth={1.5}
          className="mt-0.5 shrink-0 text-warning"
          aria-hidden="true"
        />
        <div className="text-sm text-foreground">
          <p className="font-medium">Este contenido afecta un proceso automatizado.</p>
          <p className="mt-1 text-muted-foreground">
            Cada documento <span className="font-medium text-foreground">activo</span> se inyecta en el prompt que VIA
            usa para diseñar iniciativas al convertir la demanda. Un documento mal redactado, con datos incorrectos o
            contradictorios, puede degradar la calidad de todas las conversiones siguientes. Edita con cuidado y
            desactiva en vez de borrar si tienes dudas.
          </p>
        </div>
      </div>

      {/* Usage summary */}
      <div className="mt-5 flex flex-wrap gap-3 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-muted-foreground">
          <HugeiconsIcon icon={BookOpen01Icon} size={14} strokeWidth={1.5} aria-hidden="true" />
          Usado por: <span className="font-medium text-foreground">Conversión de demanda con IA</span>
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-muted-foreground">
          Documentos activos: <span className="font-medium text-foreground">{activeCount}</span>
        </span>
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {docs === null ? (
          <>
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </>
        ) : docs.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
              <HugeiconsIcon
                icon={BookOpen01Icon}
                size={28}
                strokeWidth={1.5}
                className="text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-foreground">Aún no hay documentos de contexto</p>
              <p className="max-w-md text-xs text-muted-foreground">
                Agrega documentos con lineamientos de la empresa sobre IA (políticas de datos, proveedores autorizados,
                criterios de riesgo, ejes de valor) para que VIA genere mejores iniciativas.
              </p>
            </CardContent>
          </Card>
        ) : (
          docs.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-foreground">{doc.title}</p>
                    <Badge variant="outline" className="border-border text-[11px] text-muted-foreground">
                      {doc.category}
                    </Badge>
                    {doc.active ? (
                      <Badge className="bg-success/10 text-success border-success/20 text-[11px]">Activo</Badge>
                    ) : (
                      <Badge variant="outline" className="border-border text-[11px] text-muted-foreground">
                        Inactivo
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">{doc.content}</p>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {doc.updated_by ? `Actualizado por ${doc.updated_by}` : 'Actualizado'} · {fmtDate(doc.updated_at)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditing(doc)}
                    aria-label={`Editar ${doc.title}`}>
                    <HugeiconsIcon icon={Edit02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setConfirmDelete(doc)}
                    aria-label={`Eliminar ${doc.title}`}>
                    <HugeiconsIcon icon={Delete02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {editing && (
        <EditDialog
          doc={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento de contexto</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que quieres eliminar <span className="font-medium text-foreground">{confirmDelete?.title}</span>?
              Las próximas conversiones ya no usarán este contexto. Esta acción es permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/ai-context/${confirmDelete.id}`);
                  toast.success('Documento eliminado');
                  setConfirmDelete(null);
                  load();
                } catch (e) {
                  toast.error(apiError(e));
                }
              }}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function EditDialog({ doc, onClose, onSaved }) {
  const [form, setForm] = useState(doc ? { ...doc } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isNew = !doc;
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category?.trim() || 'General',
        content: form.content ?? '',
        active: !!form.active,
      };
      if (isNew) {
        await api.post('/ai-context', payload);
        toast.success('Documento creado');
      } else {
        await api.put(`/ai-context/${doc.id}`, payload);
        toast.success('Documento actualizado');
      }
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isNew ? 'Nuevo documento de contexto' : 'Editar documento'}
          </DialogTitle>
          <DialogDescription>
            Este contenido se inyecta en las conversiones de demanda con IA. Escríbelo como lineamiento claro y
            verificado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ctx-title" className="text-xs">
                Título
              </Label>
              <Input
                id="ctx-title"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="Ej: Proveedores de IA autorizados"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ctx-category" className="text-xs">
                Categoría
              </Label>
              <Input
                id="ctx-category"
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                placeholder="Ej: Datos, Riesgo, Proveedores, Valor"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ctx-content" className="text-xs">
              Contenido
            </Label>
            <Textarea
              id="ctx-content"
              value={form.content}
              onChange={(e) => set('content', e.target.value)}
              placeholder="Lineamientos, políticas, criterios o cualquier contexto institucional que ayude a VIA a diseñar mejores iniciativas..."
              rows={12}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label htmlFor="ctx-active" className="text-sm font-medium text-foreground">
                Activo
              </Label>
              <p className="text-xs text-muted-foreground">Solo los documentos activos se usan en las conversiones.</p>
            </div>
            <Switch id="ctx-active" checked={!!form.active} onCheckedChange={(v) => set('active', v)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving && (
              <HugeiconsIcon
                icon={Loading02Icon}
                size={16}
                strokeWidth={1.5}
                className="mr-1.5 animate-spin"
                aria-hidden="true"
              />
            )}
            {isNew ? 'Crear' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
